import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { MeetingRoomIcon } from '@modernloop/shared/icons';

import { ActivityLogTaskMeetingRoomRsvp_ActivityLogFragment, ResponseStatus } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import IconWithRsvp from 'src/entities/common/IconWithRsvp';

import ActivityLogBase from '../../Helpers/ActivityLogBase';
import ActivityLogContent from '../../Helpers/ActivityLogContent';

interface Fragments {
  activityLog: ActivityLogTaskMeetingRoomRsvp_ActivityLogFragment;
}

const ActivityLogTaskMeetingRoomRsvp: FCWithFragments<Fragments> = ({ activityLog }) => {
  const { details } = activityLog;

  // eslint-disable-next-line no-underscore-dangle
  if (!details || details.__typename !== 'ActivityLogTaskMeetingRoomDetails') {
    throw new Error('ActivityLogTaskMeetingRoomRsvp: details is not ActivityLogTaskMeetingRoomDetails');
  }

  const { interviewName, comment, responseStatus, room } = details;
  const actionLabel = useMemo(() => {
    switch (responseStatus) {
      case ResponseStatus.Accepted:
        return {
          key: 'hiring-dashboard-candidates:candidates.activityLog.acceptedInterview',
          fallback: `accepted ${interviewName}`,
        };
      case ResponseStatus.Declined:
        return {
          key: 'hiring-dashboard-candidates:candidates.activityLog.declinedInterview',
          fallback: `declined ${interviewName}`,
        };
      default:
        return undefined;
    }
  }, [interviewName, responseStatus]);

  return (
    <ActivityLogBase
      actorName={room?.name ?? undefined}
      avatar={
        <IconWithRsvp
          responseStatus={responseStatus}
          interviewerRsvpIconDataTestId={`activity-log-task-meeting-room-rsvp-${responseStatus}`}
          icon={<MeetingRoomIcon />}
        />
      }
      activityLog={activityLog}
      actionLabel={actionLabel?.fallback}
      actionLabelKey={actionLabel?.key}
      actionLabelValues={{ interviewName }}
    >
      <ActivityLogContent content={comment} />
    </ActivityLogBase>
  );
};

ActivityLogTaskMeetingRoomRsvp.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogTaskMeetingRoomRsvp_activityLog on ActivityLog {
      id
      details {
        ... on ActivityLogTaskMeetingRoomDetails {
          __typename

          responseStatus
          comment
          interviewName
          room {
            id
            name
          }
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogTaskMeetingRoomRsvp;
